import React, { useState } from 'react';
import { FaStar, FaStarHalfAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

/**
 * Interactive Star Rating Component
 * Supports both input and display modes with app's color scheme
 */
const StarRating = ({
  value = 0,
  onChange = null,
  readonly = false,
  size = 'md',
  showValue = false,
  label = '',
  maxRating = 5,
  className = ''
}) => {
  const [hoverValue, setHoverValue] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Size configurations using app's design system
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const currentValue = isHovering ? hoverValue : value;
  const isInteractive = !readonly && onChange;

  const handleStarClick = (rating) => {
    if (isInteractive) {
      onChange(rating);
    }
  };

  const handleStarHover = (rating) => {
    if (isInteractive) {
      setHoverValue(rating);
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (isInteractive) {
      setIsHovering(false);
      setHoverValue(0);
    }
  };

  const renderStar = (index) => {
    const starValue = index + 1;
    const isFilled = currentValue >= starValue;
    const isHalfFilled = currentValue >= starValue - 0.5 && currentValue < starValue;

    return (
      <motion.button
        key={index}
        type="button"
        className={`
          ${sizeClasses[size]} 
          ${isInteractive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
          transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-300 rounded
          ${className}
        `}
        onClick={() => handleStarClick(starValue)}
        onMouseEnter={() => handleStarHover(starValue)}
        disabled={!isInteractive}
        whileHover={isInteractive ? { scale: 1.1 } : {}}
        whileTap={isInteractive ? { scale: 0.95 } : {}}
        aria-label={`${label ? `${label}: ` : ''}Rate ${starValue} out of ${maxRating} stars`}
      >
        {isHalfFilled ? (
          <FaStarHalfAlt 
            className={`
              ${isFilled || isHalfFilled ? 'text-yellow-400' : 'text-gray-300'}
              ${isInteractive && isHovering ? 'text-orange-400' : ''}
              drop-shadow-sm
            `}
          />
        ) : (
          <FaStar 
            className={`
              ${isFilled ? 'text-yellow-400' : 'text-gray-300'}
              ${isInteractive && isHovering && starValue <= hoverValue ? 'text-orange-400' : ''}
              drop-shadow-sm
            `}
          />
        )}
      </motion.button>
    );
  };

  return (
    <div className="flex items-center gap-2">
      {label && (
        <label className={`font-medium text-gray-700 ${textSizeClasses[size]}`}>
          {label}:
        </label>
      )}
      
      <div 
        className="flex items-center gap-1"
        onMouseLeave={handleMouseLeave}
        role={isInteractive ? "radiogroup" : "img"}
        aria-label={`${label || 'Rating'}: ${value} out of ${maxRating} stars`}
      >
        {Array.from({ length: maxRating }, (_, index) => renderStar(index))}
      </div>

      {showValue && (
        <span className={`font-medium text-gray-600 ${textSizeClasses[size]} ml-1`}>
          {value > 0 ? value.toFixed(1) : '0.0'}
        </span>
      )}

      {/* Screen reader text */}
      <span className="sr-only">
        {`${label || 'Rating'}: ${value} out of ${maxRating} stars`}
      </span>
    </div>
  );
};

export default StarRating;